/**
 * Compiled Views Schema
 * 
 * Views are the compiled output from the CIG that defines what each tab
 * should render. The PersonaComposer transforms these based on persona
 * configuration.
 * 
 * @version 2.2
 */

import { z } from 'zod'
import { BlockSchema } from './blocks'
import { ConfidenceLevelSchema, RiskPostureSchema, ReportSectionSchema } from './cig'

// ============================================================================
// Tab Definitions
// ============================================================================

export const TabIdSchema = z.enum([
  'cockpit',
  'written_report',
  'takeaways',
  'data_figures',
  'connections',
  'sources_methods',
])
export type TabId = z.infer<typeof TabIdSchema>

export const TabConfigSchema = z.object({
  id: TabIdSchema,
  label: z.string(),
  icon: z.string().optional(),
  enabled: z.boolean().default(true),
  priority: z.number().optional(), // For persona-based ordering
})
export type TabConfig = z.infer<typeof TabConfigSchema>

// ============================================================================
// Cockpit View
// ============================================================================

export const CockpitViewSchema = z.object({
  tab: z.literal('cockpit'),
  
  // Status strip at top
  status_strip: z.object({
    risk_posture: RiskPostureSchema,
    confidence: ConfidenceLevelSchema,
    last_updated: z.string(),
    what_changed: z.string().optional(),
    tags: z.array(z.string()).optional(),
  }),
  
  // Signal triad (3 key signals)
  signals: z.array(BlockSchema).max(3),
  
  // Hero visualization
  hero_viz: BlockSchema.optional(),
  
  // Verdict block
  verdict: BlockSchema.optional(),
  
  // What to watch
  watchlist_preview: z.array(z.object({
    indicator: z.string(),
    status: z.enum(['normal', 'warning', 'critical']).optional(),
    description: z.string().optional(),
  })).max(7).optional(),
  
  // Go deeper chips
  jump_chips: z.array(z.object({
    label: z.string(),
    target_section_id: z.string().optional(),
    target_tab: TabIdSchema.optional(),
    target_entity_id: z.string().optional(),
  })).optional(),
  
  // Additional blocks (persona-dependent)
  blocks: z.array(BlockSchema).optional(),
})
export type CockpitView = z.infer<typeof CockpitViewSchema>

// ============================================================================
// Written Report View
// ============================================================================

export const WrittenReportViewSchema = z.object({
  tab: z.literal('written_report'),
  
  // Metadata
  title: z.string(),
  subtitle: z.string().optional(),
  author: z.object({
    name: z.string(),
    email: z.string().optional(),
    avatar_url: z.string().optional(),
  }).optional(),
  last_updated: z.string(),
  reading_time_minutes: z.number().optional(),
  word_count: z.number().optional(),
  confidence: ConfidenceLevelSchema,
  
  // BLUF
  bluf: z.string(),
  
  // Executive Summary (structured bullets)
  executive_summary: z.array(z.object({
    text: z.string(),
    citation_refs: z.array(z.string()).optional(), // Source IDs
  })),
  
  // Table of Contents (auto-generated from sections)
  toc_enabled: z.boolean().default(true),
  
  // Figure gallery reference
  figure_gallery_enabled: z.boolean().default(true),
  figures: z.array(z.object({
    id: z.string(),
    number: z.number(),
    title: z.string(),
    caption: z.string(),
    type: z.string(),
  })).optional(),
  
  // Main body sections (structured, not markdown)
  sections: z.array(ReportSectionSchema),
  
  // Counterpoints & uncertainty section
  counterpoints: z.array(z.object({
    claim: z.string(),
    counterpoint: z.string(),
    strength: z.enum(['strong', 'moderate', 'weak']).optional(),
    source_ids: z.array(z.string()).optional(),
  })).optional(),
  
  // Implications by audience
  implications: z.array(z.object({
    audience: z.string(),
    items: z.array(z.object({
      text: z.string(),
      citation_refs: z.array(z.string()).optional(),
    })),
  })).optional(),
  
  // Methods section
  methodology: z.string().optional(),
  
  // Limitations section
  limitations: z.array(z.string()).optional(),
  
  // Footnotes/sources
  footnotes: z.array(z.object({
    id: z.string(),
    number: z.number(),
    source_id: z.string(),
    text: z.string(),
  })),
  
  // Reading modes
  available_modes: z.array(z.enum(['clean', 'with_citations', 'with_annotations'])).default(['clean', 'with_citations']),
  
  // Fallback markdown (for legacy reports)
  fallback_markdown: z.string().optional(),

  // Deep research markdown (raw report)
  deep_research_markdown: z.string().optional(),
})
export type WrittenReportView = z.infer<typeof WrittenReportViewSchema>

// ============================================================================
// Takeaways View
// ============================================================================

export const TakeawaysViewSchema = z.object({
  tab: z.literal('takeaways'),
  
  // Key findings
  key_findings: z.array(z.object({
    text: z.string(),
    citation_refs: z.array(z.string()).optional(),
    importance: z.enum(['critical', 'high', 'medium']).optional(),
  })),
  
  // Stakeholder outcomes
  stakeholder_outcomes: z.array(z.object({
    stakeholder: z.string(),
    outcome: z.enum(['winner', 'loser', 'neutral', 'mixed']),
    explanation: z.string(),
    citation_refs: z.array(z.string()).optional(),
  })).optional(),
  
  // Decision options (if applicable)
  decision_options_block: BlockSchema.optional(),
  
  // Comparison grid (if applicable)
  comparison_grid_block: BlockSchema.optional(),
  
  // Pre-mortem checklist
  premortem_checklist: z.array(z.object({
    risk: z.string(),
    mitigation: z.string().optional(),
    probability: z.enum(['high', 'medium', 'low']).optional(),
  })).optional(),
  
  // Watchlist
  watchlist_block: BlockSchema.optional(),
  
  // Additional blocks
  blocks: z.array(BlockSchema).optional(),
})
export type TakeawaysView = z.infer<typeof TakeawaysViewSchema>

// ============================================================================
// Data & Figures View
// ============================================================================

export const DataFiguresViewSchema = z.object({
  tab: z.literal('data_figures'),
  
  // Figure gallery
  figures: z.array(z.object({
    id: z.string(),
    number: z.number(),
    type: z.string(),
    title: z.string(),
    caption: z.string(),
    block: BlockSchema, // The actual viz block
  })),
  
  // Datasets
  datasets: z.array(z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    row_count: z.number(),
    column_count: z.number(),
    block: BlockSchema, // DataTable block
    download_formats: z.array(z.enum(['csv', 'json', 'xlsx'])).optional(),
  })),
  figure_notes: z.array(z.string()).optional(),
  dataset_notes: z.array(z.string()).optional(),
  
  // Data dictionary
  data_dictionary: z.array(z.object({
    metric_key: z.string(),
    label: z.string(),
    definition: z.string(),
    unit: z.string().optional(),
    source_ids: z.array(z.string()).optional(),
  })).optional(),
  
  // Scenario assumptions registry
  scenario_assumptions: z.array(z.object({
    state_id: z.string(),
    state_label: z.string(),
    assumptions: z.array(z.object({
      metric_key: z.string(),
      base_value: z.string(),
      state_value: z.string(),
      rationale: z.string().optional(),
    })),
  })).optional(),

  // SIB blocks routed to this tab (stats_highlights, kpi_dashboards)
  sib_blocks: z.array(z.object({
    id: z.string(),
    type: z.string(),
    sibType: z.string(),
    title: z.string(),
    data: z.any(),
  })).optional(),
})
export type DataFiguresView = z.infer<typeof DataFiguresViewSchema>

// ============================================================================
// Connections View (Knowledge Graph)
// ============================================================================

// Enriched claim data for entity details
const ConnectionsClaimSchema = z.object({
  id: z.string(),
  text: z.string(),
  category: z.string().optional(),
  confidence: z.union([z.string(), z.number()]).optional(),
  anchors: z.array(z.object({
    source_id: z.string(),
    snippet: z.string().optional(),
    locator: z.string().optional(),
  })).optional(),
})

// Enriched metric data for entity details
const ConnectionsMetricSchema = z.object({
  id: z.string(),
  label: z.string(),
  value: z.union([z.string(), z.number(), z.object({ value: z.number(), formatted: z.string().optional() })]).optional(),
  unit: z.string().optional(),
  trend: z.enum(['up', 'down', 'stable', 'volatile']).optional(),
})

// Source reference for entity details
const ConnectionsSourceSchema = z.object({
  id: z.string(),
  title: z.string(),
  url: z.string().optional(),
  snippet: z.string().optional(),
})

export const ConnectionsViewSchema = z.object({
  tab: z.literal('connections'),
  
  // Graph data - enriched nodes with related intelligence
  nodes: z.array(z.object({
    id: z.string(),
    label: z.string(),
    type: z.string(),
    description: z.string().optional(),
    importance: z.number().optional(), // For sizing
    group: z.string().optional(),
    // Enriched data for entity details panel
    claims: z.array(ConnectionsClaimSchema).optional(),
    metrics: z.array(ConnectionsMetricSchema).optional(),
    sources: z.array(ConnectionsSourceSchema).optional(),
    aliases: z.array(z.string()).optional(),
    tags: z.array(z.string()).optional(),
    metadata: z.record(z.unknown()).optional(),
  })),
  
  // Enriched edges with relationship details
  edges: z.array(z.object({
    id: z.string(),
    source: z.string(),
    target: z.string(),
    type: z.string(),
    label: z.string().optional(),
    weight: z.number().optional(),
    description: z.string().optional(),
    anchors: z.array(z.object({
      source_id: z.string(),
      snippet: z.string().optional(),
    })).optional(),
  })),
  
  // Available filters
  entity_types: z.array(z.string()),
  relationship_types: z.array(z.string()),
  
  // Entity detail configuration
  entity_detail_blocks: z.record(z.string(), z.array(BlockSchema)).optional(), // entity_id -> blocks
  
  // Layout preferences
  default_layout: z.enum(['force', 'hierarchical', 'circular']).default('force'),
})
export type ConnectionsView = z.infer<typeof ConnectionsViewSchema>

// ============================================================================
// Sources & Methods View
// ============================================================================

export const SourcesMethodsViewSchema = z.object({
  tab: z.literal('sources_methods'),
  
  // Source list with reliability
  sources: z.array(z.object({
    id: z.string(),
    number: z.number(), // Stable citation number
    title: z.string(),
    publisher: z.string().optional(),
    authors: z.array(z.string()).optional(),
    date: z.string().optional(),
    url: z.string().optional(),
    source_type: z.string().optional(),
    reliability_score: z.number().min(0).max(1),
    reliability_tier: z.enum(['primary', 'secondary', 'tertiary']).optional(),
    reliability_rationale: z.string(),
    claims_supported: z.array(z.string()).optional(), // Claim IDs
    excerpts: z.array(z.object({
      id: z.string(),
      text: z.string(),
      page: z.number().optional(),
    })).optional(),
  })),
  
  // Evidence ledger (claim -> sources)
  evidence_ledger: z.array(z.object({
    claim_id: z.string(),
    claim_text: z.string(),
    source_ids: z.array(z.string()),
    support_types: z.record(z.string(), z.enum(['supports', 'contradicts', 'context'])), // source_id -> type
  })).optional(),
  
  // Methods summary
  methodology: z.object({
    summary: z.string(),
    search_strategy: z.string().optional(),
    analysis_approach: z.string().optional(),
    tools_used: z.array(z.string()).optional(),
  }).optional(),
  
  // Limitations and open questions
  limitations_block: BlockSchema.optional(),
  
  // Change log
  change_log_block: BlockSchema.optional(),
})
export type SourcesMethodsView = z.infer<typeof SourcesMethodsViewSchema>

// ============================================================================
// Compiled Views Container
// ============================================================================

export const CompiledViewsSchema = z.object({
  schema_version: z.literal('2.2'),
  report_id: z.string(),
  
  // Tab configuration (order, enabled state)
  tabs: z.array(TabConfigSchema),
  
  // Views
  cockpit: CockpitViewSchema,
  written_report: WrittenReportViewSchema,
  takeaways: TakeawaysViewSchema,
  data_figures: DataFiguresViewSchema,
  connections: ConnectionsViewSchema,
  sources_methods: SourcesMethodsViewSchema,
  
  // Scenario state (runtime)
  active_scenario_state: z.string().optional(),
})
export type CompiledViews = z.infer<typeof CompiledViewsSchema>

// ============================================================================
// Default Tab Configuration
// ============================================================================

export const DEFAULT_TAB_CONFIG: TabConfig[] = [
  { id: 'cockpit', label: 'Cockpit', icon: 'dashboard', enabled: true, priority: 1 },
  { id: 'written_report', label: 'Written Report', icon: 'document', enabled: true, priority: 2 },
  { id: 'takeaways', label: 'Takeaways', icon: 'lightbulb', enabled: true, priority: 3 },
  { id: 'data_figures', label: 'Data & Figures', icon: 'chart', enabled: true, priority: 4 },
  { id: 'connections', label: 'Connections', icon: 'network', enabled: true, priority: 5 },
  { id: 'sources_methods', label: 'Sources & Methods', icon: 'book', enabled: true, priority: 6 },
]



